import React, { createContext, useContext, useMemo, FC } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { joinPath } from './utils';

/**
 * Named regex patterns supported by `paramConstraints`. Extend this union and
 * add a matching entry to `CONSTRAINT_REGEX` to introduce a new constraint.
 */
export type ConstraintType = 'uuid';

const CONSTRAINT_REGEX: Record<ConstraintType, RegExp> = {
  uuid: /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/,
};

/**
 * Literal-value constraint. The URL only matches one of the named values, and
 * `buildRouteHelper` narrows the param's TypeScript type to the literal union
 * declared in `values`.
 *
 * @example { type: 'enum', values: ['Details', 'Overview'] as const }
 */
export interface EnumConstraint<V extends string = string> {
  type: 'enum';
  values: readonly V[];
}

export type ParamConstraint = ConstraintType | EnumConstraint;

const isEnumConstraint = (c: ParamConstraint): c is EnumConstraint =>
  typeof c === 'object' && c !== null && c.type === 'enum';

const constraintToRegex = (c: ParamConstraint): RegExp => {
  if (isEnumConstraint(c)) {
    // Alternation of literal values. Values are simple identifiers in practice;
    // escape just in case to avoid corrupting the path regex.
    const escaped = c.values.map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    return new RegExp(escaped.join('|'));
  }
  return CONSTRAINT_REGEX[c];
};

/**
 * Per-param constraint, keyed by param name and valued by either a named
 * pattern (e.g. `{ guid: 'uuid' }`) or an `EnumConstraint` for literal value
 * sets. On the current v5 renderer the regex is inlined into the route path
 * (`/:guid([0-9a-f]{8}-...)`). On the future v6 renderer the regex is removed
 * from the path and validated post-match.
 */
export type ParamConstraints = Record<string, ParamConstraint>;

/**
 * Schema for a redirect entry. Renders as a `<Redirect>` in the v5 Switch
 * (and as `<Navigate>` on the future v6 renderer).
 */
export interface RedirectRoute {
  /** Stable, unique-per-level key for React reconciliation. Required. */
  key: string;
  redirect: {
    /** Pattern that triggers the redirect. Omit for an unconditional fallback. */
    from?: string;
    /** Destination path (params from `from` are interpolated by react-router). */
    to: string;
    exact?: boolean;
    push?: boolean;
  };
}

/**
 * Schema for a route that renders a page component. The page is responsible
 * for its own layout shell, auth guards, and any other wrappers — the
 * renderer does not compose them.
 *
 * Paths are absolute at the top level and relative when nested under a parent
 * (do not include `${parentPath}/...` in child `path` strings — the renderer
 * resolves child paths against the parent's matched path).
 */
export interface LeafRoute {
  /** Stable, unique-per-level key for React reconciliation. Required. */
  key: string;
  /** Route path. Relative when nested. Must be a string — array paths are not supported. */
  path: string;
  /** v5-only. Ignored by the future v6 renderer (v6 routes are exact by default). */
  exact?: boolean;
  /** Page component. Instantiated with no props. */
  Page: React.ComponentType;
  /** Names of trailing optional params (e.g. `['subNav']` for `/foo/:subNav?`). */
  optionalParams?: readonly string[];
  /** Per-param regex constraints (e.g. `{ guid: 'uuid' }`). */
  paramConstraints?: ParamConstraints;
  /** Nested routes. Their `path` is relative to this route's path. */
  children?: readonly (LeafRoute | RedirectRoute)[];
}

export type RouteDef = LeafRoute | RedirectRoute;

const isRedirect = (def: RouteDef): def is RedirectRoute => 'redirect' in def;

const ChildRoutesContext = createContext<readonly RouteDef[] | null>(null);

/**
 * Apply `paramConstraints` and `optionalParams` to a path string by transforming
 * `:name` tokens into `:name(regex)?` as appropriate. Param names are matched
 * with a non-word lookahead so `:foo` doesn't accidentally match inside `:fooBar`.
 */
const composePath = ({
  path,
  paramConstraints,
  optionalParams,
}: {
  path: string;
  paramConstraints?: ParamConstraints;
  optionalParams?: readonly string[];
}): string => {
  const constraintNames = paramConstraints ? Object.keys(paramConstraints) : [];
  const optionalNames = optionalParams ?? [];
  const names = Array.from(new Set([...constraintNames, ...optionalNames])).sort((a, b) => b.length - a.length);
  if (names.length === 0) return path;

  return names.reduce((acc, name) => {
    const constraintSpec = paramConstraints?.[name];
    const constraint = constraintSpec ? constraintToRegex(constraintSpec) : undefined;
    const isOptional = optionalNames.includes(name);
    const replacement = `:${name}${constraint ? `(${constraint.source})` : ''}${isOptional ? '?' : ''}`;
    return acc.replace(new RegExp(`:${name}(?![A-Za-z0-9_])`, 'g'), replacement);
  }, path);
};

const extractParamNames = (path: string): Set<string> => {
  const matches = path.matchAll(/:([A-Za-z_][A-Za-z0-9_]*)/g);
  return new Set(Array.from(matches, (m) => m[1]));
};

/**
 * Throws if any route at this level is missing a `key`, has duplicate `key`
 * with a sibling, has a non-string `path`, declares `optionalParams` /
 * `paramConstraints` for names that do not appear in `path`, or (when nested)
 * declares a path that starts with `/`. Called once per Switch level (root +
 * each Outlet).
 */
const validateLevel = ({
  defs,
  levelLabel,
  isNested = false,
}: {
  defs: readonly RouteDef[];
  levelLabel: string;
  isNested?: boolean;
}): void => {
  const seen = new Map<string, number>();
  defs.forEach((def, index) => {
    const { key } = def;
    const where = `${levelLabel}[${index}]`;
    if (typeof key !== 'string' || key.length === 0) {
      const what = isRedirect(def) ? `redirect -> ${def.redirect.to}` : `path ${JSON.stringify(def.path)}`;
      throw new Error(
        `RenderRoutes: route at ${where} (${what}) is missing a "key"; every route must declare a stable, unique-per-level key.`
      );
    }
    if (seen.has(key)) {
      throw new Error(
        `RenderRoutes: duplicate route key "${key}" at ${levelLabel} (indices ${seen.get(
          key
        )} and ${index}); keys must be unique among siblings.`
      );
    }
    seen.set(key, index);

    if (isRedirect(def)) {
      if (isNested && def.redirect.from?.startsWith('/')) {
        throw new Error(
          `RenderRoutes: nested redirect "${key}" at ${where} has "from" "${def.redirect.from}" that starts with "/"; nested redirect "from" paths must be relative to the parent route.`
        );
      }
      return;
    }

    if (typeof def.path !== 'string') {
      throw new Error(
        `RenderRoutes: route "${key}" at ${where} has a non-string "path" (${JSON.stringify(
          def.path
        )}); array paths are not supported — declare a separate route entry per path.`
      );
    }
    if (isNested && def.path.startsWith('/')) {
      throw new Error(
        `RenderRoutes: nested route "${key}" at ${where} has path "${def.path}" that starts with "/"; child paths must be relative to the parent route (drop the leading slash).`
      );
    }
    const pathParams = extractParamNames(def.path);
    const declared = [
      ...(def.optionalParams ?? []),
      ...(def.paramConstraints ? Object.keys(def.paramConstraints) : []),
    ];
    const missing = Array.from(new Set(declared.filter((n) => !pathParams.has(n))));
    if (missing.length > 0) {
      throw new Error(
        `RenderRoutes: route "${key}" at ${where} declares params ${JSON.stringify(
          missing
        )} (via optionalParams / paramConstraints) that do not appear in path "${def.path}".`
      );
    }
  });
};

const hydrateRoute = ({ def, parentPath }: { def: RouteDef; parentPath: string }): JSX.Element => {
  if (isRedirect(def)) {
    const { from, to, exact, push } = def.redirect;
    const resolvedFrom = from === undefined ? undefined : joinPath(parentPath, from);
    return (
      <Redirect
        key={def.key}
        {...(resolvedFrom === undefined ? {} : { from: resolvedFrom })}
        to={to}
        exact={exact}
        push={push}
      />
    );
  }

  const joined = joinPath(parentPath, def.path);
  const composed = composePath({
    path: joined,
    paramConstraints: def.paramConstraints,
    optionalParams: def.optionalParams,
  });
  const { Page } = def;
  return (
    <Route key={def.key} path={composed} exact={def.exact}>
      <ChildRoutesContext.Provider value={def.children ?? null}>
        <Page />
      </ChildRoutesContext.Provider>
    </Route>
  );
};

/**
 * Placeholder for nested child routes. Mirrors react-router v6's `<Outlet />`.
 *
 * Drop this in the parent page where the matched child should render. The
 * `renderRoutes` call that produced the parent route makes its `children`
 * available via context; `<Outlet />` resolves each child's relative path
 * against the parent's matched path and emits a descendant `<Switch>`.
 *
 * When v6 lands, this can be replaced by re-exporting `Outlet` from
 * `react-router-dom` without consumer changes.
 */
export const Outlet: FC = () => {
  const children = useContext(ChildRoutesContext);
  const match = useRouteMatch();
  if (!children || children.length === 0) return null;
  validateLevel({ defs: children, levelLabel: `Outlet under "${match.path}"`, isNested: true });
  return (
    <ChildRoutesContext.Provider value={null}>
      <Switch>{children.map((child) => hydrateRoute({ def: child, parentPath: match.path }))}</Switch>
    </ChildRoutesContext.Provider>
  );
};

/**
 * Render a route configuration as a v5 `<Switch>` of `<Route>` / `<Redirect>`
 * entries. Designed to be swapped for a v6 renderer over the same schema
 * without consumers changing their route records.
 *
 * Page components are responsible for their own layout and guard wrappers —
 * the renderer does not compose them.
 *
 * Memoized: if `routeDefs` is referentially stable across renders, the
 * component's render body (including key validation) is skipped.
 *
 * @example
 * ```tsx
 * const Accounts = () => (
 *   <AdminShell><GuardedRoute><AccountsPage /></GuardedRoute></AdminShell>
 * );
 *
 * const routes: RouteDef[] = [
 *   { key: 'home', path: '/', exact: true, Page: Home },
 *   {
 *     key: 'project',
 *     path: '/projects/:projectId',
 *     Page: ProjectDetail,
 *     paramConstraints: { projectId: 'uuid' },
 *     children: [
 *       { key: 'submissions', path: 'submissions', Page: Submissions },
 *       { key: 'lists', path: 'lists/:listId', optionalParams: ['listId'], Page: Lists },
 *     ],
 *   },
 *   { key: 'accounts', path: '/accounts', Page: Accounts },
 *   { key: 'fallback', redirect: { to: '/projects' } },
 * ];
 *
 * export const Routes = () => <RenderRoutes routeDefs={routes} />;
 * ```
 */
export interface RenderRoutesProps {
  routeDefs: readonly RouteDef[];
}

export const RenderRoutes: FC<RenderRoutesProps> = React.memo(({ routeDefs }) => {
  useMemo(() => validateLevel({ defs: routeDefs, levelLabel: 'RenderRoutes root', isNested: false }), [routeDefs]);
  return <Switch>{routeDefs.map((def) => hydrateRoute({ def, parentPath: '' }))}</Switch>;
});
RenderRoutes.displayName = 'RenderRoutes';
